import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Brush,
  ResponsiveContainer,
  Scatter,
} from "recharts";
import { Loader } from "lucide-react";
import { DEVICE_HISTORY_API_URL } from "@/constants/system";
import { format } from "date-fns";

interface SolarHistoryRecord {
  deviceLastUpdated: string;
  ambientLux?: number;
  battery?: {
    batterySoC?: number;
    chargeVin?: number;
  };
  deviceStatus?: string;
}

interface ChartDataPoint {
  time: string;
  timestamp: number;
  batterySoC: number | null;
  chargeVin: number | null;
  ambientLux: number | null;
  deviceStatus: string | null;
  isRecord: number | null;
  isRecordOnDemand: number | null;
}

interface SolarPerformanceChartProps {
  deviceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-background p-3 shadow-md text-xs space-y-1">
      <p className="font-medium">{label}</p>
      {payload
        .filter((entry: any) => entry.dataKey !== "isRecord" && entry.dataKey !== "isRecordOnDemand")
        .map((entry: any) => (
        <div key={entry.dataKey} className="flex items-center gap-2">
          <div
            className="h-2 w-2 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-muted-foreground">{entry.name}:</span>
          <span className="font-medium">
            {entry.value != null ? entry.value : "—"}
          </span>
        </div>
      ))}
      {payload[0]?.payload?.deviceStatus && (
        <div className="pt-1 border-t text-muted-foreground">
          Status: {payload[0].payload.deviceStatus}
        </div>
      )}
    </div>
  );
};

type SeriesKey = "batterySoC" | "chargeVin" | "ambientLux";

const SERIES_CONFIG: Record<SeriesKey, { label: string; color: string; unit: string; axisLabel: string }> = {
  batterySoC: { label: "Battery SoC (%)", color: "hsl(142, 71%, 45%)", unit: "%", axisLabel: "SoC (%)" },
  chargeVin: { label: "Solar Charge (V)", color: "hsl(25, 95%, 53%)", unit: "V", axisLabel: "Solar Charge (V)" },
  ambientLux: { label: "Ambient Lux", color: "hsl(48, 96%, 53%)", unit: "lux", axisLabel: "Lux" },
};

export const SolarPerformanceChart = ({
  deviceId,
  open,
  onOpenChange,
}: SolarPerformanceChartProps) => {
  const [data, setData] = useState<ChartDataPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [visibleSeries, setVisibleSeries] = useState<Record<SeriesKey, boolean>>({
    batterySoC: true,
    chargeVin: true,
    ambientLux: true,
  });

  const toggleSeries = (key: SeriesKey) => {
    setVisibleSeries((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    if (!open) return;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          DEVICE_HISTORY_API_URL,
          {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: new URLSearchParams({ deviceID: deviceId }),
          }
        );
        if (!response.ok) throw new Error("Failed to fetch device history");
        const records: SolarHistoryRecord[] = await response.json();

        const chartData: ChartDataPoint[] = records
          .map((r) => {
            const ts = new Date(r.deviceLastUpdated).getTime();
            if (isNaN(ts)) return null;
            const status = r.deviceStatus || null;
            return {
              time: format(new Date(ts), "dd/MM HH:mm"),
              timestamp: ts,
              batterySoC: r.battery?.batterySoC ?? null,
              chargeVin: r.battery?.chargeVin ?? null,
              ambientLux: (r as any).ambientLux ?? null,
              deviceStatus: status,
              isRecord: status === "Record" ? 0 : null,
              isRecordOnDemand: status === "RecordOnDemand" ? 0 : null,
            } as ChartDataPoint;
          })
          .filter(Boolean)
          .sort((a, b) => a!.timestamp - b!.timestamp) as ChartDataPoint[];

        setData(chartData);
      } catch (err: any) {
        setError(err.message || "Unknown error");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [open, deviceId]);

    const maxLux = Math.max(...data.map((d) => d.ambientLux ?? 0), 0);
    const luxCeiling = Math.ceil(maxLux / 100) * 100 || 100;

    // Calculate right margin based on how many right-side axes are visible
  const rightAxesCount = [visibleSeries.chargeVin, visibleSeries.ambientLux].filter(Boolean).length;
  const rightMargin = rightAxesCount > 1 ? 60 : 10;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[90vw] md:max-w-[80vw] max-h-[85vh]">
        <DialogHeader>
          <DialogTitle>Solar & Battery Performance</DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {error && (
          <div className="text-destructive text-sm text-center py-8">
            {error}
          </div>
        )}

        {!loading && !error && data.length > 0 && (
          <div className="space-y-3">
            {/* Series toggle buttons */}
            <div className="flex flex-wrap gap-2">
              {(Object.keys(SERIES_CONFIG) as SeriesKey[]).map((key) => {
                const cfg = SERIES_CONFIG[key];
                const active = visibleSeries[key];
                return (
                  <button
                    key={key}
                    onClick={() => toggleSeries(key)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                      active
                        ? "border-transparent text-white"
                        : "border-border text-muted-foreground bg-muted/30 opacity-50"
                    }`}
                    style={active ? { backgroundColor: cfg.color } : undefined}
                  >
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: cfg.color }}
                    />
                    {cfg.label}
                  </button>
                );
              })}
            </div>

            <div className="w-full h-[400px] md:h-[500px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={data}
                  margin={{ top: 10, right: rightMargin, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border/50" />
                  <XAxis
                    dataKey="time"
                    tick={{ fontSize: 10 }}
                    interval="preserveStartEnd"
                    className="fill-muted-foreground"
                  />

                  {/* Battery SoC axis - left */}
                  {visibleSeries.batterySoC && (
                    <YAxis
                      yAxisId="soc"
                      orientation="left"
                      tick={{ fontSize: 10 }}
                      label={{
                        value: "SoC (%)",
                        angle: -90,
                        position: "insideLeft",
                        dx: 15,
                        style: { fontSize: 10, fill: "hsl(142, 71%, 45%)" },
                      }}
                      stroke="hsl(142, 71%, 45%)"
                      domain={[0, 100]}
                    />
                  )}

                  {/* Charge Voltage axis - right */}
                  {visibleSeries.chargeVin && (
                    <YAxis
                      yAxisId="voltage"
                      orientation="right"
                      tick={{ fontSize: 10 }}
                      label={{
                        value: "Solar Charge (V)",
                        angle: 90,
                        position: "insideRight",
                        dx: -15,
                        style: { fontSize: 10, fill: "hsl(25, 95%, 53%)" },
                      }}
                      stroke="hsl(25, 95%, 53%)"
                      domain={[0, 12]}
                    />
                  )}

                  {/* Ambient Lux axis - right (offset if voltage also visible) */}
                  {visibleSeries.ambientLux && (
                    <YAxis
                      yAxisId="lux"
                      orientation="right"
                      tick={{ fontSize: 10 }}
                      label={{
                        value: "Lux",
                        angle: 90,
                        position: "insideRight",
                        offset: visibleSeries.chargeVin ? 50 : 0,
                        style: { fontSize: 10, fill: "hsl(48, 96%, 53%)" },
                      }}
                      stroke="hsl(48, 96%, 53%)"
                      domain={[0, luxCeiling]}
                    />
                  )}

                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />

                  {visibleSeries.batterySoC && (
                    <Line
                      yAxisId="soc"
                      type="monotone"
                      dataKey="batterySoC"
                      name="Battery SoC (%)"
                      stroke="hsl(142, 71%, 45%)"
                      dot={false}
                      strokeWidth={2}
                      connectNulls
                    />
                  )}
                  {visibleSeries.chargeVin && (
                    <Line
                      yAxisId="voltage"
                      type="monotone"
                      dataKey="chargeVin"
                      name="Solar Charge (V)"
                      stroke="hsl(25, 95%, 53%)"
                      dot={false}
                      strokeWidth={2}
                      connectNulls
                    />
                  )}
                  {visibleSeries.ambientLux && (
                    <Line
                      yAxisId="lux"
                      type="monotone"
                      dataKey="ambientLux"
                      name="Ambient Lux"
                      stroke="hsl(48, 96%, 53%)"
                      dot={false}
                      strokeWidth={2}
                      connectNulls
                    />
                  )}
                  {visibleSeries.batterySoC && (
                    <Scatter
                      yAxisId="soc"
                      dataKey="isRecord"
                      name="Record Event"
                      fill="hsl(142, 71%, 45%)"
                      shape="circle"
                      legendType="circle"
                    />
                  )}
                  {visibleSeries.batterySoC && (
                    <Scatter
                      yAxisId="soc"
                      dataKey="isRecordOnDemand"
                      name="On-Demand Event"
                      fill="hsl(200, 80%, 50%)"
                      shape="diamond"
                      legendType="diamond"
                    />
                  )}
                  <Brush
                    dataKey="time"
                    height={25}
                    stroke="hsl(var(--primary))"
                    travellerWidth={8}
                    className="text-xs"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {!loading && !error && data.length === 0 && (
          <div className="text-muted-foreground text-sm text-center py-8">
            No data available for this device.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
